import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

// ── EpisodeBurst ──────────────────────────────────────────────────────────────
// A fast, subtle particle pop at the tap point whenever an episode is marked
// watched — the most frequent action in the app, so it stays quick (~0.7s) and
// small, never blocking. Mark handlers dispatch:
//   window.dispatchEvent(new CustomEvent('nextep:ep-marked', { detail: { x, y } }))
// where x/y are screen coords (use the button's bounding-rect center).
export function EpisodeBurstListener() {
  const [bursts, setBursts] = useState([])

  useEffect(() => {
    let id = 0
    const onMark = (e) => {
      const { x, y } = e.detail || {}
      if (typeof x !== 'number' || typeof y !== 'number') return
      const bid = ++id
      setBursts(b => [...b, { id: bid, x, y }])
      setTimeout(() => setBursts(b => b.filter(z => z.id !== bid)), 760)
    }
    window.addEventListener('nextep:ep-marked', onMark)
    return () => window.removeEventListener('nextep:ep-marked', onMark)
  }, [])

  if (!bursts.length) return null

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 'var(--z-sheet)' }}>
      {bursts.map(b => (
        <div key={b.id} style={{ position: 'absolute', left: b.x, top: b.y }}>
          {/* check pulse */}
          <span style={{
            position: 'absolute', left: -11, top: -11, width: 22, height: 22, borderRadius:'var(--radius-full)',
            border: '2px solid var(--green)', '--nxa': 'epbRing 0.55s ease-out forwards',
          }}/>
          {/* particles */}
          {Array.from({ length: 7 }).map((_, i) => {
            const ang = (i / 7) * Math.PI * 2
            const dx  = Math.cos(ang) * 24
            const dy  = Math.sin(ang) * 24
            return (
              <span key={i} style={{
                position: 'absolute', left: -3, top: -3, width: 6, height: 6, borderRadius:'var(--radius-full)',
                background: i % 2 ? 'var(--green)' : 'var(--amber)',
                '--dx': `${dx}px`, '--dy': `${dy}px`,
                '--nxa': 'epbParticle 0.7s var(--ease-out) forwards',
              }}/>
            )
          })}
        </div>
      ))}
      <style>{`
        @keyframes epbParticle {
          0%   { opacity: 1; transform: translate(0,0) scale(1); }
          100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.3); }
        }
        @keyframes epbRing {
          0%   { opacity: 0.9; transform: scale(0.4); }
          100% { opacity: 0; transform: scale(1.8); }
        }
      `}</style>
    </div>,
    document.body
  )
}

// Helper for mark handlers: dispatch a burst at the center of the tapped element.
export function fireEpisodeBurst(e) {
  try {
    const el = e?.currentTarget
    if (!el?.getBoundingClientRect) return
    const r = el.getBoundingClientRect()
    window.dispatchEvent(new CustomEvent('nextep:ep-marked', {
      detail: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
    }))
  } catch {}
}
